import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';

interface DemoSessionInfo {
  workspaceId: string;
  threadId: string;
  demoAccessKey: string;
  timestamp: number;
}

const DEMO_STORAGE_KEY = 'kolega_demo_session';
const DEMO_EXPIRY_HOURS = 24; // Demo info expires after 24 hours

/**
 * Hook to detect and manage demo mode state throughout the application.
 * Handles URL detection, localStorage persistence, and demo session migration.
 */
export const useDemoMode = () => {
  const [searchParams] = useSearchParams();

  const demoAccessKey = searchParams.get('demoaccesskey');
  const isDemoMode = Boolean(demoAccessKey);

  /**
   * Extract demo info from current URL if it matches demo pattern
   * URL pattern: /project/{workspaceId}/thread/{threadId}?demoaccesskey={key}
   */
  const detectAndStoreDemoFromURL = () => {
    try {
      const url = new URL(window.location.href);
      const pathname = url.pathname;
      const searchParams = url.searchParams;
      
      // Check if URL matches demo pattern
      const workspaceMatch = pathname.match(/\/project\/([^/]+)\/thread\/([^/]+)/);
      const demoAccessKey = searchParams.get('demoaccesskey');
      
      if (workspaceMatch && demoAccessKey) {
        const [, workspaceId, threadId] = workspaceMatch;
        
        const demoInfo: DemoSessionInfo = {
          workspaceId,
          threadId,
          demoAccessKey,
          timestamp: Date.now()
        };
        
        localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demoInfo));
        console.log('Demo session detected and stored:', { workspaceId, threadId, demoAccessKey });
        
        return demoInfo;
      }
      
      return null;
    } catch (error) {
      console.error('Error detecting demo from URL:', error);
      return null;
    }
  };

  /**
   * Get stored demo info from localStorage (if not expired)
   */
  const getStoredDemoInfo = (): DemoSessionInfo | null => {
    try {
      const stored = localStorage.getItem(DEMO_STORAGE_KEY);
      if (!stored) return null;
      
      const demoInfo: DemoSessionInfo = JSON.parse(stored);
      
      // Check if expired (older than 24 hours)
      const isExpired = Date.now() - demoInfo.timestamp > (DEMO_EXPIRY_HOURS * 60 * 60 * 1000);
      if (isExpired) {
        localStorage.removeItem(DEMO_STORAGE_KEY);
        console.log('Stored demo session expired, removed from localStorage');
        return null;
      }
      
      return demoInfo;
    } catch (error) {
      console.error('Error retrieving stored demo info:', error);
      localStorage.removeItem(DEMO_STORAGE_KEY); // Clear corrupted data
      return null;
    }
  };

  /**
   * Clear stored demo info from localStorage
   */
  const clearStoredDemoInfo = () => {
    localStorage.removeItem(DEMO_STORAGE_KEY);
    console.log('Demo session info cleared from localStorage');
  };

  /**
   * Check if there's a pending demo migration
   */
  const hasPendingDemoMigration = (): boolean => {
    return getStoredDemoInfo() !== null;
  };

  /**
   * Migrate stored demo session to authenticated user
   */
  const migratePendingDemoSession = async (token: string): Promise<boolean> => {
    const demoInfo = getStoredDemoInfo();
    if (!demoInfo) {
      console.log('No pending demo session to migrate');
      return false;
    }

    try {
      console.log(`Attempting to migrate demo session`, demoInfo);
      
      const result = await api.post(`/demo/migrate?demo_access_key=${demoInfo.demoAccessKey}`, null, {
        token
      });

      console.log('Migration successful:', result);
      
      // Clear localStorage after successful migration
      clearStoredDemoInfo();
      
      // Navigate to the migrated workspace using the correct URL format
      const newUrl = `/project/${demoInfo.workspaceId}/thread/${demoInfo.threadId}`;
      window.location.href = newUrl;
      
      return true;
    } catch (error) {
      console.error('Error during demo migration:', error);
      // Clear localStorage even on failure - demo session is no longer valid
      clearStoredDemoInfo();
      return false;
    }
  };

  return {
    isDemoMode,
    demoAccessKey,
    detectAndStoreDemoFromURL,
    getStoredDemoInfo,
    clearStoredDemoInfo,
    hasPendingDemoMigration,
    migratePendingDemoSession,
  };
};